<template>
  <section class="gold_top_rule relative min-w-0 rounded-lg border border-border-strong bg-bg-surface p-4 shadow-md" :aria-labelledby="headingId">
    <header class="flex items-start justify-between gap-3">
      <span class="min-w-0">
        <h2 :id="headingId" class="font-display text-sm font-semibold uppercase tracking-wide text-text-primary">{{ title }}</h2>
        <p class="mt-1 text-xs leading-relaxed text-text-muted">{{ description }}</p>
      </span>
      <span class="shrink-0 text-2xs font-mono text-text-muted">{{ messages.manager.count(presets.length, MAX_BUILD_PRESETS) }}</span>
    </header>

    <p v-if="loading" class="mt-3 text-xs text-text-muted" aria-live="polite">{{ messages.manager.loading }}</p>
    <p v-else-if="error !== null" class="mt-3 rounded-sm border border-danger/40 bg-danger/10 px-2 py-1.5 text-xs text-danger" role="alert">{{ error }}</p>

    <form v-if="editorOpen" class="mt-3 flex flex-col gap-3 rounded-md border border-accent/40 bg-bg-elevated p-3" @submit.prevent="onSubmit">
      <label class="flex flex-col gap-1 text-xs font-semibold text-text-secondary">
        <span>{{ messages.manager.name }}</span>
        <AppInput
          v-model="draftName"
          :placeholder="messages.manager.namePlaceholder"
          :input-aria-label="messages.manager.name"
          :disabled="busy"
          :error="showValidation && !validName"
          required
          autofocus
        />
      </label>

      <div class="grid gap-2 sm:grid-cols-2">
        <label v-for="kind in BUILD_PRESET_STAGE_KINDS" :key="kind" class="flex min-w-0 flex-col gap-1 text-xs font-semibold text-text-secondary">
          <span>{{ messages.stages[kind] }}</span>
          <span class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
            <AppSelect
              :model-value="selectedKey(kind)"
              :options="buildOptions"
              :disabled="busy"
              :error="showValidation && selectedPair(kind) === null"
              :placeholder="messages.manager.build"
              :aria-label="messages.manager.selectBuild(messages.stages[kind])"
              @update:model-value="(value) => onBuildSelected(kind, value)"
            />
            <span class="inline-flex min-w-14 items-center justify-center gap-0.5 rounded-sm border border-border bg-bg-surface p-1">
              <img
                v-for="slug in selectedPair(kind) ?? []"
                :key="`${kind}-${slug}`"
                :src="weaponIcon(slug)"
                :alt="weaponName(slug)"
                class="h-6 w-6 shrink-0 rounded-sm object-cover"
                draggable="false"
              >
            </span>
          </span>
        </label>
      </div>

      <p v-if="showValidation && !validDraft" class="text-xs text-danger" role="alert">{{ messages.manager.invalid }}</p>
      <div class="flex justify-end gap-2">
        <AppButton :variant="ButtonVariant.Ghost" :size="ButtonSize.Sm" :disabled="busy" @click="closeEditor">{{ messages.manager.cancel }}</AppButton>
        <AppButton :type="ButtonType.Submit" :size="ButtonSize.Sm" :disabled="busy">{{ editingPresetId === null ? messages.manager.create : messages.manager.save }}</AppButton>
      </div>
    </form>

    <template v-else>
      <div v-if="presets.length > 0" class="mt-3 flex flex-col gap-2" role="list">
        <article v-for="preset in presets" :key="preset.id" class="rounded-md border border-border bg-bg-elevated p-2" role="listitem">
          <header class="mb-2 flex items-center justify-between gap-2">
            <span class="min-w-0 truncate font-mono text-xs font-semibold text-text-primary">{{ preset.name }}</span>
            <span class="flex shrink-0 items-center gap-1">
              <button type="button" class="px-1.5 py-0.5 text-xs text-text-secondary hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent" @click="startEdit(preset)">{{ messages.manager.edit }}</button>
              <button type="button" class="px-1.5 py-0.5 text-xs text-danger hover:text-danger/80 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent" @click="onRemove(preset)">{{ messages.manager.remove }}</button>
            </span>
          </header>
          <BuildPresetPreview :preset="preset" />
        </article>
      </div>
      <p v-else-if="!loading" class="mt-3 text-xs text-text-muted">{{ messages.manager.empty }}</p>

      <p v-if="availablePairs.length === 0" class="mt-3 rounded-sm border border-warning/30 bg-warning/10 px-2 py-1.5 text-xs text-warning">{{ messages.manager.noBuilds }}</p>
      <AppButton
        class="mt-3 w-full"
        :variant="ButtonVariant.Secondary"
        :size="ButtonSize.Sm"
        :disabled="availablePairs.length === 0 || presets.length >= MAX_BUILD_PRESETS"
        @click="startCreate"
      >{{ messages.manager.add }}</AppButton>
    </template>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue'

import { WEAPONS_BY_SLUG } from '@/data/weapons'
import { AppButton, AppInput, AppSelect, ButtonSize, ButtonType, ButtonVariant } from '@/shared/ui'
import type { AppSelectOption } from '@/shared/ui'
import { weaponDisplayName } from '../../lib/roleLabels'
import { uniqueWeaponPairs, weaponPairKey } from '../../lib/buildPresets'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import {
  BUILD_PRESET_STAGE_KINDS,
  BuildPresetSource,
  MAX_BUILD_PRESETS,
  MAX_NAME_LEN,
} from '../../types'
import type { BuildPreset, BuildPresetSource as PresetSource, BuildPresetStage, RaidStageKind } from '../../types'
import BuildPresetPreview from './BuildPresetPreview.vue'
import { useBuildPresetMessages } from './locales'

export interface BuildPresetManagerProps {
  readonly scope: PresetSource
}

const props = defineProps<BuildPresetManagerProps>()
const store = useGvgPlannerStore()
const messages = useBuildPresetMessages()
const headingId = `build-preset-manager-${useId()}`
const editorOpen = ref<boolean>(false)
const editingPresetId = ref<string | null>(null)
const draftName = ref<string>('')
const selectedKeys = ref<ReadonlyMap<RaidStageKind, string>>(new Map())
const busy = ref<boolean>(false)
const showValidation = ref<boolean>(false)

const isPersonal = computed<boolean>(() => props.scope === BuildPresetSource.Personal)
const title = computed<string>(() => isPersonal.value ? messages.value.manager.personalTitle : messages.value.manager.guildTitle)
const description = computed<string>(() => isPersonal.value ? messages.value.manager.personalDescription : messages.value.manager.guildDescription)
const presets = computed<ReadonlyArray<BuildPreset>>(() => isPersonal.value ? store.myBuildPresets : store.guildBuildPresets)
const loading = computed<boolean>(() => isPersonal.value && store.myBuildPresetsLoading)
const error = computed<string | null>(() => isPersonal.value ? store.myBuildPresetsError : null)

const availablePairs = computed<ReadonlyArray<ReadonlyArray<string>>>(() => {
  if (isPersonal.value) return uniqueWeaponPairs(store.myBuilds)
  return uniqueWeaponPairs(store.players.flatMap((player) =>
    player.guildBuilds.map((build) => build.weaponSlugs)))
})
const editorPairs = computed<ReadonlyArray<ReadonlyArray<string>>>(() => {
  const editing = presets.value.find((preset) => preset.id === editingPresetId.value)
  return uniqueWeaponPairs([
    ...availablePairs.value,
    ...(editing?.stages.map((stage) => stage.weaponSlugs) ?? []),
  ])
})
const buildOptions = computed<ReadonlyArray<AppSelectOption>>(() =>
  editorPairs.value.map((pair, index) => ({
    value: weaponPairKey(pair),
    text: messages.value.manager.buildWithWeapons(
      index + 1,
      weaponName(pair[0] ?? ''),
      weaponName(pair[1] ?? ''),
    ),
  })),
)
const validName = computed<boolean>(() => {
  const name = draftName.value.trim()
  return name.length > 0 && Array.from(name).length <= MAX_NAME_LEN
})
const validDraft = computed<boolean>(() => validName.value
  && BUILD_PRESET_STAGE_KINDS.every((kind) => selectedPair(kind) !== null))

function weaponName(slug: string): string {
  const weapon = WEAPONS_BY_SLUG.get(slug)
  return weapon === undefined ? slug : weaponDisplayName(slug, weapon.name, messages.value.names)
}

function weaponIcon(slug: string): string | undefined {
  return WEAPONS_BY_SLUG.get(slug)?.icon
}

function selectedKey(kind: RaidStageKind): string | null {
  return selectedKeys.value.get(kind) ?? null
}

function selectedPair(kind: RaidStageKind): ReadonlyArray<string> | null {
  const key = selectedKey(kind)
  return key === null
    ? null
    : editorPairs.value.find((pair) => weaponPairKey(pair) === key) ?? null
}

function onBuildSelected(kind: RaidStageKind, value: string | number | null): void {
  if (typeof value !== 'string') return
  selectedKeys.value = new Map([...selectedKeys.value, [kind, value]])
}

function startCreate(): void {
  const first = availablePairs.value[0]
  if (first === undefined) return
  const key = weaponPairKey(first)
  editingPresetId.value = null
  draftName.value = ''
  selectedKeys.value = new Map(BUILD_PRESET_STAGE_KINDS.map((kind) => [kind, key]))
  showValidation.value = false
  editorOpen.value = true
}

function startEdit(preset: BuildPreset): void {
  editingPresetId.value = preset.id
  draftName.value = preset.name
  selectedKeys.value = new Map(preset.stages.map((stage) => [stage.stageKind, weaponPairKey(stage.weaponSlugs)]))
  showValidation.value = false
  editorOpen.value = true
}

function closeEditor(): void {
  if (busy.value) return
  editorOpen.value = false
  editingPresetId.value = null
  showValidation.value = false
}

function draftStages(): ReadonlyArray<BuildPresetStage> {
  return BUILD_PRESET_STAGE_KINDS.flatMap((stageKind) => {
    const weaponSlugs = selectedPair(stageKind)
    return weaponSlugs === null ? [] : [{ stageKind, weaponSlugs }]
  })
}

async function onSubmit(): Promise<void> {
  showValidation.value = true
  if (!validDraft.value || busy.value) return
  busy.value = true
  const name = draftName.value.trim()
  const stages = draftStages()
  const id = editingPresetId.value
  const succeeded = isPersonal.value
    ? id === null
      ? await store.createMyBuildPreset(name, stages)
      : await store.updateMyBuildPreset(id, name, stages)
    : id === null
      ? await store.createGuildBuildPreset(name, stages)
      : await store.updateGuildBuildPreset(id, name, stages)
  busy.value = false
  if (succeeded) closeEditor()
}

async function onRemove(preset: BuildPreset): Promise<void> {
  if (busy.value || !window.confirm(messages.value.manager.removeConfirm(preset.name))) return
  busy.value = true
  if (isPersonal.value) {
    await store.removeMyBuildPreset(preset.id)
  } else {
    await store.removeGuildBuildPreset(preset.id)
  }
  busy.value = false
}
</script>
